/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CollabTokenResponse } from '../models/collab-token-response';
import { CollabWsResponse } from '../models/collab-ws-response';
import { JwksResponse } from '../models/jwks-response';

@Injectable({
  providedIn: 'root',
})
export class CollabService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getCollabToken
   */
  static readonly GetCollabTokenPath = '/api/collab/token';

  /**
   * Issues a short-lived RS256 token for authenticating with the Collaboration Service.
   * Valid for 30 seconds. Payload contains only the caller's userId as `sub`.
   * The JWT header includes `kid` matching the key published at /api/collab/jwks.json.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCollabToken()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCollabToken$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CollabTokenResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CollabService.GetCollabTokenPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CollabTokenResponse>;
      })
    );
  }

  /**
   * Issues a short-lived RS256 token for authenticating with the Collaboration Service.
   * Valid for 30 seconds. Payload contains only the caller's userId as `sub`.
   * The JWT header includes `kid` matching the key published at /api/collab/jwks.json.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCollabToken$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCollabToken(params?: {
  },
  context?: HttpContext

): Observable<CollabTokenResponse> {

    return this.getCollabToken$Response(params,context).pipe(
      map((r: StrictHttpResponse<CollabTokenResponse>) => r.body as CollabTokenResponse)
    );
  }

  /**
   * Path part for operation getJwks
   */
  static readonly GetJwksPath = '/api/collab/jwks.json';

  /**
   * Returns the public key set used to verify collab tokens.
   * Unauthenticated — intended for the Collaboration Service to fetch on startup.
   * The Collaboration Service should validate x5c against ca.crt before trusting the key.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getJwks()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJwks$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<JwksResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CollabService.GetJwksPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<JwksResponse>;
      })
    );
  }

  /**
   * Returns the public key set used to verify collab tokens.
   * Unauthenticated — intended for the Collaboration Service to fetch on startup.
   * The Collaboration Service should validate x5c against ca.crt before trusting the key.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getJwks$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJwks(params?: {
  },
  context?: HttpContext

): Observable<JwksResponse> {

    return this.getJwks$Response(params,context).pipe(
      map((r: StrictHttpResponse<JwksResponse>) => r.body as JwksResponse)
    );
  }

  /**
   * Path part for operation getWsUrl
   */
  static readonly GetWsUrlPath = '/api/collab/ws';

  /**
   * Returns a ready-to-use WebSocket URL including a short-lived collab token.
   * Optionally scoped to a specific island, with echo and throttle flags.
   * If client_id is not supplied, one is generated and returned alongside the URL.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getWsUrl()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWsUrl$Response(params?: {

    /**
     * Optional island ID to scope the WebSocket connection
     */
    islandId?: string;

    /**
     * Optional client UUID — generated if not provided
     */
    clientId?: string;

    /**
     * When true, the client receives its own broadcast messages
     */
    echo?: boolean;

    /**
     * When true, outgoing messages are batched in a 15ms aggregation window
     */
    throttle?: boolean;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CollabWsResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CollabService.GetWsUrlPath, 'get');
    if (params) {
      rb.query('islandId', params.islandId, {});
      rb.query('clientId', params.clientId, {});
      rb.query('echo', params.echo, {});
      rb.query('throttle', params.throttle, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CollabWsResponse>;
      })
    );
  }

  /**
   * Returns a ready-to-use WebSocket URL including a short-lived collab token.
   * Optionally scoped to a specific island, with echo and throttle flags.
   * If client_id is not supplied, one is generated and returned alongside the URL.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getWsUrl$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWsUrl(params?: {

    /**
     * Optional island ID to scope the WebSocket connection
     */
    islandId?: string;

    /**
     * Optional client UUID — generated if not provided
     */
    clientId?: string;

    /**
     * When true, the client receives its own broadcast messages
     */
    echo?: boolean;

    /**
     * When true, outgoing messages are batched in a 15ms aggregation window
     */
    throttle?: boolean;
  },
  context?: HttpContext

): Observable<CollabWsResponse> {

    return this.getWsUrl$Response(params,context).pipe(
      map((r: StrictHttpResponse<CollabWsResponse>) => r.body as CollabWsResponse)
    );
  }

}
